"use client";

import Link from "next/link";
import AckoDriveLogo from "@components/atoms/AckoDriveLogo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/atoms/Accordion";
import styles from "./Footer.module.scss";
import Image from "next/image";
import { useBreadcrumb } from "@context/BreadcrumbContext";
import assetLoader from "@utils/internalImageLoder";
import { useHasBottomNav } from "@hooks/useHasBottomNav";
import { useHideFooter } from "@hooks/use-hide-footer";
import { usePathname, useRouter } from "next/navigation";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { useEffect } from "react";
import CustomLink from "@components/atoms/Link";

const socialConfig = [
  {
    href: "https://youtube.com/@ackodrive/",
    title: "Visit ACKO Drive on YouTube",
    icon: "youtube-icon.svg",
    alt: "YouTube icon"
  },
  {
    href: "https://instagram.com/ackodrive/",
    title: "Visit ACKO Drive on Instagram",
    icon: "instagram-icon.svg",
    alt: "Instagram icon"
  },
  {
    href: "https://x.com/ackodrive/",
    title: "Visit ACKO Drive on X (formerly Twitter)",
    icon: "twitter-updated-icon.svg",
    alt: "Twitter icon"
  },
  {
    href: "https://www.facebook.com/ackodrive/",
    title: "Visit ACKO Drive on Facebook",
    icon: "facebook-icon.svg",
    alt: "Facebook icon"
  }
];
export default function Footer({ isLoggedIn, city }: any) {
  const router = useRouter();
  const { breadcrumbs } = useBreadcrumb();
  const hasBottomNav = useHasBottomNav();
  const hideFooter = useHideFooter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  if (hideFooter) {
    return null;
  }

  const isStickyCTA =
    pathname?.startsWith("/cars/") &&
    !pathname?.includes("/colours") &&
    !pathname?.includes("/faqs") &&
    !pathname?.includes("/price") &&
    !pathname?.includes("/images") &&
    !pathname?.includes("/specifications") &&
    !pathname?.includes("/mileage") &&
    !pathname?.includes("/review") &&
    !pathname?.includes("/variants") &&
    !pathname?.includes("/dimensions");

  let loggedInStickyCTA = false;
  let loggedOutStickyCTA = false;

  if (isStickyCTA) {
    if (isLoggedIn) {
      loggedInStickyCTA = true;
    } else {
      loggedOutStickyCTA = true;
    }
  }

  const companyLinks = [
    { name: "About Us", href: "/about-us/" },
    { name: "Contact Us", href: "/contact-us/" },
    { name: "Group company", href: "https://www.acko.com/" },
  ];

  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy-policy/" },
    { name: "Terms and Conditions", href: "/terms-and-conditions/" },
    { name: "Car Insurance", href: "https://www.acko.com/car-insurance/" },
    { name: "Bike Insurance", href: "https://www.acko.com/two-wheeler-insurance/" },
    { name: "Acko Health Insurance", href: "https://www.acko.com/health-insurance/" },
    { name: "Acko Life Insurance", href: "https://www.acko.com/life-insurance/" },
    { name: "Acko Term Insurance", href: "https://www.acko.com/life-insurance/term-insurance/" },
    { name: "Group Health Insurance", href: "https://www.acko.com/group-health-insurance/" },
    { name: "Travel Insurance", href: "https://www.acko.com/travel-insurance/" },
    { name: "Visa", href: "https://www.acko.com/visa/" },
  ];

  return (
    <footer
      className={`${styles.footer} ${
        hasBottomNav ? styles.hasBottomNavPadding : ""
      } ${loggedOutStickyCTA ? styles.loggedOutStickyCTA : ""} ${
        loggedInStickyCTA ? styles.loggedInStickyCTA : ""
      }`}
    >
      {breadcrumbs && !(pathname == "/") && (
        <div className={styles.breadcrumbsContainer}>
          <nav className={styles.breadcrumbs}>
            {breadcrumbs?.map((item: any, index: any) => (
              <div key={index} className={styles.breadcrumbItem}>
                {item.current ? (
                  <span className={styles.breadcrumbCurrent}>{item.name}</span>
                ) : (
                  <CustomLink
                    href={item.href}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();
                      showLoader(`Loading...`);
                      router.push(item.href);
                    }}
                    className={styles.breadcrumbLink}
                    segmentEventProps={{
                      eventName: "action",
                      pageType: "MM page",
                      eventProperties: {
                        action_type: "tap_btn",
                        action_name: "tap_btn_breadcrumbs",
                        breadcrumb_name: item.name,
                        breadcrumb_href: item.href,
                        city_name: city?.name,
                        vehicle_make: item.vehicleMake,
                        vehicle_model: item.vehicleModel,
                      },
                    }}
                  >
                    {item.name}
                  </CustomLink>
                )}
                {index < breadcrumbs?.length - 1 && (
                  <Image
                    src={`${assetLoader()}/icons/Chevron_right_light.svg`}
                    alt="Chevron right"
                    width={20}
                    height={20}
                    className={styles.breadcrumbChevron}
                    loading="lazy"
                    sizes="20px"
                  />
                )}
              </div>
            ))}
          </nav>
        </div>
      )}

      <div className={styles.footerContainer}>
        <div className={styles.desktopFooter}>
          <div className={styles.desktopFooterColumn}>
            <Link href="/" className={styles.logoLink}>
              <AckoDriveLogo className={styles.ackoDriveLogo} variant="dark" />
            </Link>
            <p className={styles.addressText}>
              {`36/5, Hustlehub One East, Somasandrapalya, 27th Main Rd, Sector 2, HSR Layout, Bengaluru, Karnataka 560102.`}
            </p>
            <div className={styles.socialLinks}>
              {socialConfig.map((social) => (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={social.title}
                >
                  <Image
                    src={`${assetLoader()}/images/footer/${social.icon}`}
                    loading="lazy"
                    alt={social.alt}
                    width={32}
                    height={32}
                    sizes="32px"
                    className={styles.socialIcon}
                  />
                </a>
              ))}
            </div>
          </div>

          <div className={styles.desktopFooterDivider}></div>

          <div className={styles.desktopFooterColumn}>
            <h3 className={styles.companyHeading}>COMPANY</h3>
            <ul className={styles.companyLinksList}>
              {companyLinks.map((link, index) => (
                <li key={index}>
                  <Link href={link.href} className={styles.companyLink}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.desktopFooterDivider}></div>

          <div className={styles.desktopFooterColumn}>
            <h3 className={styles.legalHeading}>LEGAL</h3>
            <ul className={styles.legalLinksList}>
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link href={link.href} className={styles.legalLink} prefetch={false}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={styles.mobileFooter}>
          <div className={styles.mobileFooterTop}>
            <div className={styles.logoAddress}>
              <Link href="/" className={styles.logoLink}>
                <AckoDriveLogo
                  className={styles.ackoDriveLogo}
                  variant="dark"
                />
              </Link>
              <p className={styles.addressText}>
                {`36/5, Hustlehub One East, Somasandrapalya, 27th Main Rd, Sector 2,
                HSR Layout, Bengaluru, Karnataka 560102.`}
              </p>
            </div>
            <div className={styles.socialLinks}>
              {socialConfig.map((social) => (
                <a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={social.title}
                >
                  <Image
                    src={`${assetLoader()}/images/footer/${social.icon}`}
                    loading="lazy"
                    alt={social.alt}
                    width={24}
                    height={24}
                    priority={false}
                    sizes="24px"
                    className={styles.socialIcon}
                  />
                </a>
              ))}
            </div>
          </div>

          <div className={styles.mobileFooterDivider}></div>

          <div className={styles.mobileFooterAccordion}>
            <Accordion type="multiple" className={styles.accordion}>
              <AccordionItem value="company" className={styles.accordionItem}>
                <AccordionTrigger
                  value="company"
                  className={styles.accordionTrigger}
                >
                  COMPANY
                </AccordionTrigger>
                <AccordionContent
                  value="company"
                  className={styles.accordionContent}
                >
                  <ul className={styles.companyLinksList}>
                    {companyLinks.map((link, index) => (
                      <li key={index}>
                        <Link href={link.href} className={styles.companyLink}>
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="legal" className={styles.accordionItem}>
                <AccordionTrigger
                  value="legal"
                  className={styles.accordionTrigger}
                >
                  LEGAL
                </AccordionTrigger>
                <AccordionContent
                  value="legal"
                  className={styles.accordionContent}
                >
                  <ul className={styles.legalLinksList}>
                    {legalLinks.map((link, index) => (
                      <li key={index}>
                        <Link href={link.href} className={styles.legalLink}>
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </footer>
  );
}
